import classNames from 'classnames';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { forwardRef } from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Tag from '@/components/molecules/tag/tag';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import styles from './calendar-slider-tile.module.scss';

export interface CalendarSliderTileProps {
  /** Indicates whether the tile is active */
  active?: boolean;
  /** Indicates whether to show the charter flight tag */
  charterFlight?: boolean;
  /** The class name to apply */
  className?: string;
  /** The date */
  date?: Date;
  /** Indicates whether the tile is disabled */
  disabled?: boolean;
  /** Indicates whether the tile is in a loading state */
  isLoading?: boolean;
  /** An event that is triggered on click, the tile is a button without a url */
  onClick?: () => void;
  /** Starting price to show */
  price?: string;
  /** Whether to show price information */
  showDetails?: boolean;
  /** Whether the tile is in the tab order, -1 for all but one in the slider */
  tabIndex?: number;
  /** The number of trips found that match the search  */
  tripNumber?: number;
  /** The url to link to */
  url?: string;
  /** The week number */
  weekNumber?: number;
}

const CalendarSliderTile = forwardRef<
  HTMLAnchorElement,
  CalendarSliderTileProps
>(
  (
    {
      active = false,
      charterFlight = false,
      className,
      date,
      isLoading = false,
      onClick,
      price,
      showDetails,
      tabIndex,
      tripNumber,
      url,
      weekNumber,
    }: CalendarSliderTileProps,
    ref,
  ) => {
    const { t } = useI18n();
    const formattedDate = useDateFormatter([weekOfYear]).dateFormatter(date);

    if (isLoading) {
      return (
        <div
          className={classNames(styles.component, className, styles.isLoading)}
        >
          <div className={styles.contentWrapper}>
            <div className={styles.content}>
              <SkeletonLoader borderRadius="4px" height="20px" width="80%" />

              <SkeletonLoader borderRadius="4px" height="15px" width="55%" />
            </div>

            <div className={styles.content}>
              <SkeletonLoader borderRadius="4px" height="15px" width="40%" />

              <SkeletonLoader borderRadius="4px" height="15px" width="70%" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <Link
        ref={ref}
        // A button (without url) exposes whether its date is the selected one
        aria-pressed={!url && onClick ? active : undefined}
        className={classNames(styles.component, className, {
          [styles.isActive]: active,
          [styles.isCharterFlight]: charterFlight,
        })}
        onClick={onClick}
        tabIndex={tabIndex}
        url={url}
      >
        {charterFlight && (
          <Tag
            className={styles.charterFlight}
            text={t('items.common.words.charterFlight')}
          />
        )}

        {showDetails ? (
          <div className={styles.contentWrapper}>
            {date && (
              <div className={styles.content}>
                <Typography className={styles.label} tag="span" tagStyle="date">
                  {formattedDate.format('D MMM')}
                </Typography>

                <Typography tagStyle="bodyMedium">
                  {capitalizeFirstLetter(formattedDate.format('ddd'))}
                  {', '}
                  {t('items.common.words.week')} {weekNumber && weekNumber}
                </Typography>
              </div>
            )}

            <div className={styles.content}>
              <Typography tagStyle="bodyMedium">
                {`${tripNumber} ${t('items.common.words.trips')}`}
              </Typography>

              {price && (
                <Typography className={styles.label} tagStyle="bodyMediumBold">
                  {price}
                </Typography>
              )}
            </div>
          </div>
        ) : (
          <div className={classNames(styles.contentWrapper, styles.isMinimal)}>
            {date && (
              <div className={styles.content}>
                <Typography
                  className={styles.label}
                  tag="span"
                  tagStyle="headingSmall"
                >
                  {formattedDate.format('D')}
                </Typography>

                <Typography
                  className={styles.label}
                  tag="span"
                  tagStyle="headingSmall"
                >
                  {capitalizeFirstLetter(formattedDate.format('MMM'))}
                </Typography>

                <Typography tagStyle="bodyMedium">
                  {capitalizeFirstLetter(formattedDate.format('dddd'))}
                  {', '}
                  {t('items.common.words.week')} {weekNumber && weekNumber}
                </Typography>
              </div>
            )}
          </div>
        )}
      </Link>
    );
  },
);

CalendarSliderTile.displayName = 'CalendarSliderTile';

export default CalendarSliderTile;
